import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, LineChart, CalendarHeart, Users, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: Dashboard,
});

const greet = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const tiles = [
  { to: "/chat", title: "Talk to Sage", desc: "Your AI companion is here, any hour.", icon: MessageCircle },
  { to: "/mood", title: "Log your mood", desc: "Notice how today actually feels.", icon: LineChart },
  { to: "/counseling", title: "Anonymous counseling", desc: "Start a private thread.", icon: Users },
  { to: "/therapists", title: "Book a therapist", desc: "Verified clinicians, flexible times.", icon: CalendarHeart },
] as const;

function Dashboard() {
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return null;
      const { data } = await supabase.from("profiles").select("*").eq("id", u.user.id).maybeSingle();
      return data;
    },
  });

  const { data: lastMood } = useQuery({
    queryKey: ["last-mood"],
    queryFn: async () => {
      const { data } = await supabase.from("mood_entries").select("score, created_at").order("created_at", { ascending: false }).limit(1).maybeSingle();
      return data;
    },
  });

  return (
    <div className="space-y-10">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 text-accent" />
          You are {profile?.anonymous_handle ?? "anonymous"} here.
        </div>
        <h1 className="text-4xl md:text-5xl text-gradient">{greet()}. How is your heart today?</h1>
        {lastMood ? (
          <p className="text-sm text-muted-foreground">Last mood logged: <span className="text-foreground">{lastMood.score}/10</span></p>
        ) : (
          <p className="text-sm text-muted-foreground">You haven't logged a mood yet. Even one number helps.</p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {tiles.map((t) => (
          <Link key={t.to} to={t.to} className="glass rounded-3xl p-6 group hover:border-primary/40 hover:glow-primary transition">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-secondary/60 flex items-center justify-center group-hover:bg-accent/20 transition">
                <t.icon className="h-6 w-6 text-accent" />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl">{t.title}</h3>
                <p className="text-sm text-muted-foreground">{t.desc}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="glass rounded-3xl p-8 text-center space-y-3">
        <h2 className="text-2xl">A small reminder</h2>
        <p className="text-muted-foreground max-w-md mx-auto text-sm">
          Reaching out is brave. Whatever you choose to do today — talk, journal, breathe, or simply rest — it counts.
        </p>
      </div>
    </div>
  );
}
